import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NotificationService } from '@app/core/infrastructure/notifications';
import { IButton, ModalType, NotificationConfig, NotificationDialogData } from '@app/shared/types/ui';
import { take } from 'rxjs';

@Component({
  selector: 'app-notification-dialog',
  standalone: true,
  imports: [NgOptimizedImage, MatDialogModule, MatIconModule, MatButtonModule],
  template: `
    <div class="notification-dialog__header">
      <h2 mat-dialog-title class="notification-dialog__title">{{ config.title }}</h2>
      <button mat-icon-button class="notification-dialog__close" color="warn" (click)="onClose()" aria-label="Cerrar notificación">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content class="notification-dialog__content">
      @if (config) {
        @switch (config.contentType) {
          @case ('image') {
            <div class="notification-dialog__media">
              <img [ngSrc]="config.content" class="notification-dialog__image" alt="Contenido de notificación" priority/>
            </div>
          }
          @case ('video') {
            <div class="notification-dialog__media">
              <iframe
                [src]="sanitizedUrl"
                width="100%"
                height="200"
                class="notification-dialog__video"
                frameborder="0"
                allowfullscreen>
              </iframe>
            </div>
          }
          @case ('text') {
            <div class="notification-dialog__text">
              {{ config.content }}
            </div>
          }
        }
      }
    </mat-dialog-content>

    @if (config.footer) {
      <div class="notification-dialog__footer-text text-muted">
        {{ config.footer }}
      </div>
    }

    <mat-dialog-actions align="end" class="notification-dialog__actions">
      @for (btn of buttons; track btn.action) {
        <button mat-button [class]="'notification-dialog__button notification-dialog__button--' + btn.action" (click)="onButtonClick(btn)">
          {{ btn.text }}
        </button>
      }
    </mat-dialog-actions>
  `,
  styles: [
    `
    .notification-dialog__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: var(--accent-200) !important;
      color: rgba(255, 255, 255, 0.87) !important;
    }

    .notification-dialog__close {
      margin-right: 6px;
    }

    .notification-dialog__title {
      flex: 1;
      margin: 0;
      text-align: center;
      font-weight: 500;
    }

    .notification-dialog__content {
      background-color: var(--accent-200) !important;
      color: rgba(255, 255, 255, 0.87) !important;
    }

    .notification-dialog__media {
      display: flex;
      justify-content: center;
      margin-bottom: 16px;
    }

    .notification-dialog__image {
      max-height: 300px;
      max-width: 100%;
      border-radius: 6px;
      object-fit: contain;
    }

    .notification-dialog__video {
      border-radius: 6px;
    }

    .notification-dialog__text {
      text-align: left;
      font-size: 14px;
      line-height: 1.5;
      padding: 12px 0;
      color: var(--mat-dialog-supporting-text-color);
    }

    .notification-dialog__footer-text {
      font-size: 12px;
      padding: 0px 24px;
      text-align: left;
      opacity: 0.7;
    }

    .notification-dialog__actions {
      background-color: var(--accent-200) !important;
      color: rgba(255, 255, 255, 0.87) !important;
      padding: 12px 24px;
      margin-bottom: 0;
    }

    .notification-dialog__button {
      border-radius: 10px;
      padding: 7px 35px;
      font-weight: 600;
      border: solid 2px  var(--primary-500);
    }

    .notification-dialog__button--accept,
    .notification-dialog__button--redirect {
      background-color: var(--primary-500);
      color: white;
    }

    .notification-dialog__button--cancel {
      color:  var(--primary-500);
    }

    .text-muted {
      color: rgba(0, 0, 0, 0.6);
    }
  `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationDialogComponent implements OnInit {
  private readonly data = inject<NotificationDialogData>(MAT_DIALOG_DATA);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly dialogRef = inject(MatDialogRef<NotificationDialogComponent>);
  private readonly notificationService = inject(NotificationService);
  protected config!: NotificationConfig;
  protected buttons: IButton[] = [];
  protected sanitizedUrl?: SafeResourceUrl;

  ngOnInit() {
    if (this.data.notification) {
      this.initNotification(this.data.notification);
    }
  }

  private initNotification(cfg: NotificationConfig) {
    this.config = {
      ...cfg,
      state: cfg.state ?? { accepted: false, opened: 0 }
    };

    const raw = cfg.data;
    const buttonsArray: IButton[] = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const actionPriority: Record<string, number> = {
      cancel: 0,
      accept: 1,
      redirect: 2
    };

    this.buttons = buttonsArray.sort((a, b) =>
      (actionPriority[a.action] ?? 99) - (actionPriority[b.action] ?? 99)
    );

    if (cfg.contentType === 'video') {
      this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(cfg.content);
    }
  }

  protected onClose(): void {
    if (this.config.modalType !== ModalType.INTERACTIVE && this.config.manyTimes) {
      this.incrementOpenCount();
    }
    this.dialogRef.close();
  }

  protected onButtonClick(btn: IButton): void {
    switch (this.config.modalType) {
      case ModalType.INTERACTIVE: this.handleInteractiveClick(btn); break;
      case ModalType.REDIRECT: this.handleRedirectClick(btn); break;
      case ModalType.INFORMATIVE: this.handleInformativeClick(); break;
    }
    this.dialogRef.close();
  }

  private initialize(cfg: NotificationConfig): void {
    this.config = { ...cfg, state: cfg.state ?? { accepted: false, opened: 0 } };

    const rawButtons = typeof cfg.data === 'string' ? JSON.parse(cfg.data) : cfg.data;
    this.buttons = this.sortButtons(rawButtons);

    if (cfg.contentType === 'video') {
      this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(cfg.content);
    }
  }

  private sortButtons(buttons: IButton[]): IButton[] {
    const priority: Record<string, number> = { cancel: 0, accept: 1, redirect: 2 };
    return [...buttons].sort((a, b) => (priority[a.action] ?? 99) - (priority[b.action] ?? 99));
  }

  private handleInteractiveClick(btn: IButton): void {
    if (btn.action === 'accept' && this.config.state) {
      this.config.state.accepted = true;
    }
    if (this.config.manyTimes) {
      this.incrementOpenCount();
    }
  }

  private handleRedirectClick(btn: IButton): void {
    if (this.config.manyTimes) this.incrementOpenCount();
    if (btn.action === 'redirect' && btn.redirectUrl) {
      window.open(btn.redirectUrl, '_blank');
    }
  }

  private handleInformativeClick(): void {
    if (this.config.manyTimes) this.incrementOpenCount();
  }

  private incrementOpenCount(): void {
    if (this.config.state) {
      this.config.state.opened++;
      this.updateState();
    }
  }

  private updateState(): void {
    if (!this.config.state) return;

    this.notificationService.updateState(this.config.id, this.config.state.opened, this.config.state.accepted)
      .pipe(take(1))
      .subscribe();
  }
}
